import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { orderBy } from 'lodash-es'
import { EMPTY, catchError, switchMap } from 'rxjs'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { Id } from '@/infra/id'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { tapAction, toStream } from '@/utils/mobx-util'
import { clock, withAbortSignal } from '@/utils/rx-util'
import { links } from '@/utils/link'
import type { NavigateFn } from '@/RootStore'
import type { Legend } from '@/features/legends/Legend'
import type { LegendStore } from '@/features/legends/LegendStore'
import { NewLegendDialogViewModel } from '@/features/legends/NewLegendDialog/NewLegendDialogViewModel'
import type { WorkspaceSelectorViewModel } from '@/features/legends/WorkspaceSelectorViewModel'
import { LegendListItemViewModel } from './LegendListItemViewModel'

const PAGE_SIZE = 50

/**
 * Which legends the list shows.
 */
export type LegendListFilter = 'Active' | 'All'

/**
 * View model for the legend list: the selected workspace's legends.
 */
export class LegendListViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _items = new ViewModelList({
    models: () => this._legends,
    create: (legend: Legend) =>
      new LegendListItemViewModel(
        legend,
        this._legendStore,
        () => this.workspaceSelector.canModifyLegends,
        this._showInactiveIfOtherwiseEmpty,
        () => this.now,
        this._navigate,
        this._createAlertErrorHandler,
      ),
  })

  @observable accessor filter: LegendListFilter = 'Active'
  @observable accessor searchText = ''

  /**
   * The time, kept current so that "changed 2 minutes ago" stays true.
   */
  @observable accessor now = new Date()

  readonly newLegendVm: NewLegendDialogViewModel

  /**
   * Fetches the selected workspace's legends whenever another one is picked.
   */
  private readonly _workspaceChanges$ = toStream(
    () => this.workspaceSelector.selectedWorkspace.id,
    true,
  ).pipe(
    switchMap(() =>
      withAbortSignal((signal) => this.fetch(signal)).pipe(
        catchError(() => EMPTY),
      ),
    ),
  )

  private readonly _clock$ = clock(20_000).pipe(
    tapAction((now) => {
      this.now = now
    }),
  )

  constructor(
    readonly workspaceSelector: WorkspaceSelectorViewModel,
    private readonly _legendStore: LegendStore,
    private readonly _navigate: NavigateFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    this.newLegendVm = new NewLegendDialogViewModel(
      this._createLegend,
      _createAlertErrorHandler,
    )
    this._lifecycle.manage(this._items)
    this._lifecycle.manage(this._workspaceChanges$)
    this._lifecycle.manage(this._clock$)
  }

  /**
   * The workspace's legends, active ones first, then by view order.
   */
  @computed
  private get _legends() {
    const workspaceId = this.workspaceSelector.selectedWorkspace.id
    const withLatestVersions = this._legendStore
      .legendsForWorkspace(workspaceId)
      .flatMap((legend) => {
        const latest = this._legendStore.latestVersionFor(legend.id)
        return latest ? [{ legend, latest }] : []
      })
    return orderBy(withLatestVersions, [
      ({ legend }) => (legend.inactive ? 1 : 0),
      ({ latest }) => latest.viewOrder,
    ]).map(({ legend }) => legend)
  }

  /**
   * Whether the workspace has no legends at all.
   */
  @computed
  get isEmpty() {
    return this._items.items.length === 0
  }

  /**
   * Whether we're fetching legends we don't have yet. We check the selected
   * workspace's legends, not a `Revalidation`, so switching to an unloaded
   * workspace still shows the skeleton.
   */
  @computed
  get isFetchingInitial() {
    return this.fetch.pending && this.isEmpty
  }

  @computed
  get inactiveCount() {
    return this._items.items.filter((item) => item.legend.inactive).length
  }

  /**
   * The legends that match the filter and the search text.
   */
  @computed
  get items() {
    const searchText = this.searchText.trim().toLowerCase()
    return this._items.items.filter(
      (item) =>
        (this.filter === 'All' || !item.legend.inactive) &&
        (!searchText ||
          item.latestVersion.name.toLowerCase().includes(searchText)),
    )
  }

  @computed
  get isSearchFilterApplied() {
    return this.searchText.trim() !== ''
  }

  /**
   * Whether the user may create legends in the selected workspace.
   */
  @computed
  get canCreateLegends() {
    return this.workspaceSelector.canModifyLegends
  }

  /**
   * Fetches every legend of the selected workspace.
   */
  fetch = task(async (signal?: AbortSignal) => {
    const workspaceId = this.workspaceSelector.selectedWorkspace.id
    if (!Id.some(workspaceId)) {
      return
    }

    const fetchSignal = signal
      ? AbortSignal.any([signal, this._lifecycle.signal])
      : this._lifecycle.signal
    // Every page, since the filter, search and order apply to the whole list
    // here rather than on the server.
    let after: string | null = null
    do {
      const page = await this._legendStore.listLegends(
        { workspaceId, limit: PAGE_SIZE, after },
        fetchSignal,
      )
      after = page.next
    } while (after)

    this._showInactiveIfOtherwiseEmpty()
  })

  /**
   * Fetches the legends again after a failed load. The view renders the
   * outcome from {@link fetch}.
   */
  @action.bound
  retry() {
    this.fetch().catch(() => undefined)
  }

  @action.bound
  setFilter(filter: LegendListFilter) {
    this.filter = filter
  }

  @action.bound
  setSearchText(searchText: string) {
    this.searchText = searchText
  }

  @action.bound
  clearSearchText() {
    this.searchText = ''
  }

  /** @inheritDoc */
  activate() {
    // The workspace is picked first, so the first fetch is for it.
    this.workspaceSelector.activate()
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.workspaceSelector.deactivate()
  }

  /**
   * Shows the inactive legends when there are no active ones.
   */
  @action.bound
  private _showInactiveIfOtherwiseEmpty() {
    const items = this._items.items
    if (items.length > 0 && items.every((item) => item.legend.inactive)) {
      this.filter = 'All'
    }
  }

  private _createLegend = async (name: string) => {
    const legend = await this._legendStore.createLegend({
      name,
      workspaceId: this.workspaceSelector.selectedWorkspace.id,
    })
    this._navigate(links.legend(legend.id), { viewTransition: true })
  }
}
